import type { AudioRecNoteTemplate, AudioRecRoomProfile, RecognitionPeak } from '../types';
import { hzToMidi, midiToHz, midiToName } from '../util/note';
import { captureDecayRatio, clamp, median } from './stats';
import type { CalibrationCaptures, CalibrationPeakFrame, StepCapture } from './types';
import { centsBetween, harmonicNumberFor, isSamePitchHz } from '../recognition/pitch';

const MAX_TEMPLATE_HARMONIC = 6;
const MAX_TEMPLATE_ALIASES = 6;
const MAX_FRAME_PEAKS = 6;

interface FundamentalObservation {
  hz: number;
  midi: number;
  score: number;
}

interface CaptureTemplateSeed {
  midi: number;
  note: string;
  fundamentalHz: number;
  rmsValues: number[];
  decayRatio: number | null;
  peakFrames: CalibrationPeakFrame[];
}

const HARMONIC_SUPPORT_WEIGHTS = [0, 1.25, 0.34, 1.0, 0.9, 0.82, 0.74];

const inferFundamentalFromPeaks = (peaks: RecognitionPeak[]): FundamentalObservation | null => {
  if (peaks.length === 0) return null;

  const strongestAmp = peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  if (!Number.isFinite(strongestAmp) || strongestAmp <= 0) return null;

  const limitedPeaks = peaks.slice(0, MAX_FRAME_PEAKS);
  const candidateHzs: number[] = [];
  for (const peak of limitedPeaks) {
    for (let harmonic = 1; harmonic <= MAX_TEMPLATE_HARMONIC; harmonic++) {
      const candidateHz = peak.hz / harmonic;
      if (candidateHz < 35 || candidateHz > 2000) continue;
      if (candidateHzs.some((existingHz) => isSamePitchHz(existingHz, candidateHz, 35))) continue;
      candidateHzs.push(candidateHz);
    }
  }

  let best: FundamentalObservation | null = null;
  for (const candidateHz of candidateHzs) {
    let directRatio = 0;
    let harmonicCount = 0;
    let support = 0;

    for (const peak of limitedPeaks) {
      const harmonic = harmonicNumberFor(peak.hz, candidateHz, 55);
      if (harmonic == null || harmonic > MAX_TEMPLATE_HARMONIC) continue;
      const normalizedAmp = peak.amp / strongestAmp;
      support += normalizedAmp * (HARMONIC_SUPPORT_WEIGHTS[harmonic] ?? 0.7);
      if (harmonic === 1) {
        directRatio = Math.max(directRatio, normalizedAmp);
      } else {
        harmonicCount += 1;
      }
    }

    if (directRatio < 0.12 && harmonicCount < 2) continue;

    const score = support + harmonicCount * 0.14 + directRatio * 0.24;
    if (!best || score > best.score) {
      best = {
        hz: candidateHz,
        midi: Math.round(hzToMidi(candidateHz)),
        score,
      };
    }
  }

  return best;
};

const summarizeSingleCapture = (capture: StepCapture): CaptureTemplateSeed | null => {
  const observations = capture.peakFrames
    .map((frame) => inferFundamentalFromPeaks(frame.peaks))
    .filter((value): value is FundamentalObservation => value != null);

  if (observations.length === 0) return null;

  const midiScores = new Map<number, number>();
  for (const observation of observations) {
    midiScores.set(observation.midi, (midiScores.get(observation.midi) ?? 0) + observation.score);
  }

  let winningMidi: number | null = null;
  let winningScore = -Infinity;
  for (const [midi, score] of midiScores.entries()) {
    if (score > winningScore) {
      winningMidi = midi;
      winningScore = score;
    }
  }
  if (winningMidi == null) return null;

  const selectedHzs = observations
    .filter((observation) => observation.midi === winningMidi)
    .map((observation) => observation.hz);
  const fundamentalHz = median(selectedHzs) ?? midiToHz(winningMidi);

  return {
    midi: winningMidi,
    note: midiToName(winningMidi),
    fundamentalHz,
    rmsValues: capture.rms.slice(),
    decayRatio: captureDecayRatio(capture),
    peakFrames: capture.peakFrames.slice(),
  };
};

const buildTemplateHarmonics = (fundamentalHz: number, peakFrames: CalibrationPeakFrame[]) => {
  const harmonics: AudioRecNoteTemplate['harmonics'] = [];
  const totalFrames = Math.max(1, peakFrames.length);

  for (let harmonic = 1; harmonic <= MAX_TEMPLATE_HARMONIC; harmonic++) {
    const amplitudeRatios: number[] = [];
    const centsOffsets: number[] = [];

    for (const frame of peakFrames) {
      const strongestAmp = frame.peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
      if (!Number.isFinite(strongestAmp) || strongestAmp <= 0) continue;

      const expectedHz = fundamentalHz * harmonic;
      let bestPeak: RecognitionPeak | null = null;
      let bestDistance = Infinity;

      for (const peak of frame.peaks) {
        const distance = Math.abs(centsBetween(peak.hz, expectedHz));
        if (distance > 60 || distance >= bestDistance) continue;
        bestPeak = peak;
        bestDistance = distance;
      }

      if (!bestPeak) continue;
      amplitudeRatios.push(bestPeak.amp / strongestAmp);
      centsOffsets.push(centsBetween(bestPeak.hz, expectedHz));
    }

    if (amplitudeRatios.length === 0) continue;

    const coverage = amplitudeRatios.length / totalFrames;
    if (coverage < 0.18) continue;

    harmonics.push({
      harmonic,
      amplitudeRatio: median(amplitudeRatios) ?? 0,
      centsOffset: median(centsOffsets) ?? 0,
      coverage: clamp(coverage, 0, 1),
    });
  }

  return harmonics;
};

const buildTemplateAliases = (
  templateMidi: number,
  peakFrames: CalibrationPeakFrame[],
): AudioRecNoteTemplate['aliases'] => {
  const aliasMap = new Map<number, { weight: number; weightedHz: number }>();
  const totalFrames = Math.max(1, peakFrames.length);

  for (const frame of peakFrames) {
    const strongestAmp = frame.peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
    if (!Number.isFinite(strongestAmp) || strongestAmp <= 0) continue;

    for (const peak of frame.peaks.slice(0, 4)) {
      const midi = Math.round(hzToMidi(peak.hz));
      if (midi === templateMidi) continue;

      const weight = peak.amp / strongestAmp;
      const prev = aliasMap.get(midi) ?? { weight: 0, weightedHz: 0 };
      aliasMap.set(midi, {
        weight: prev.weight + weight,
        weightedHz: prev.weightedHz + peak.hz * weight,
      });
    }
  }

  return Array.from(aliasMap.entries())
    .map(([midi, value]) => ({
      midi,
      note: midiToName(midi),
      avgHz: value.weight > 0 ? value.weightedHz / value.weight : midiToHz(midi),
      strength: clamp(value.weight / totalFrames, 0, 2.5),
    }))
    .filter((alias) => alias.strength >= 0.18)
    .sort((a, b) => b.strength - a.strength || a.midi - b.midi)
    .slice(0, MAX_TEMPLATE_ALIASES);
};

export const buildNoteTemplates = (captures: CalibrationCaptures): AudioRecNoteTemplate[] => {
  const seeds = captures.single
    .map((capture) => summarizeSingleCapture(capture))
    .filter((seed): seed is CaptureTemplateSeed => seed != null);

  const byMidi = new Map<number, CaptureTemplateSeed[]>();
  for (const seed of seeds) {
    const group = byMidi.get(seed.midi) ?? [];
    group.push(seed);
    byMidi.set(seed.midi, group);
  }

  const templates: AudioRecNoteTemplate[] = [];
  for (const [midi, group] of byMidi.entries()) {
    const fundamentalHz = median(group.map((seed) => seed.fundamentalHz)) ?? midiToHz(midi);
    const peakFrames = group.flatMap((seed) => seed.peakFrames);
    const rmsValues = group.flatMap((seed) => seed.rmsValues);
    const decayRatios = group
      .map((seed) => seed.decayRatio)
      .filter((value): value is number => value != null && Number.isFinite(value));

    templates.push({
      midi,
      note: midiToName(midi),
      fundamentalHz,
      centsOffset: centsBetween(fundamentalHz, midiToHz(midi)),
      sampleCount: group.length,
      medianRms: median(rmsValues) ?? 0,
      decayRatio: median(decayRatios) ?? 0.45,
      harmonics: buildTemplateHarmonics(fundamentalHz, peakFrames),
      aliases: buildTemplateAliases(midi, peakFrames),
    });
  }

  return templates.sort((a, b) => a.midi - b.midi);
};

export const buildRoomProfile = (captures: CalibrationCaptures): AudioRecRoomProfile => {
  const noiseFrames = captures.noise.flatMap((capture) => capture.peakFrames);
  const bins = new Map<number, { weight: number; weightedHz: number }>();
  const totalFrames = Math.max(1, noiseFrames.length);

  for (const frame of noiseFrames) {
    const strongestAmp = frame.peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
    if (!Number.isFinite(strongestAmp) || strongestAmp <= 0) continue;

    for (const peak of frame.peaks.slice(0, 4)) {
      const binHz = Math.round(peak.hz / 5) * 5;
      const weight = peak.amp / strongestAmp;
      const prev = bins.get(binHz) ?? { weight: 0, weightedHz: 0 };
      bins.set(binHz, {
        weight: prev.weight + weight,
        weightedHz: prev.weightedHz + peak.hz * weight,
      });
    }
  }

  return {
    noisePeaks: Array.from(bins.entries())
      .map(([, value]) => ({
        hz: value.weight > 0 ? value.weightedHz / value.weight : 0,
        strength: clamp(value.weight / totalFrames, 0, 2.5),
      }))
      .filter((peak) => peak.strength >= 0.12)
      .sort((a, b) => b.strength - a.strength || a.hz - b.hz)
      .slice(0, 8),
  };
};
